import React, { useState, useEffect, useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import PropTypes from 'prop-types'
import { CCard, CImage } from '@coreui/react-pro'
import Select from 'react-select'
import './FacilityInfoCard.scss'
import tick from '../../../assets/icons/Encounter-Progress.svg'
import aiTick from '../../../assets/icons/Encounter-ProgressAI.svg'
import { getFacilities } from 'src/redux/census/censusThunk'
import PlaceOfServiceSelector from './PlaceOfServiceSelector'
import { listPlaceOfService } from '../../../helper/backendHelper'
import { isEmpty } from 'lodash'
import { getPOSDisplay, updateTelehealthPOS } from '../../common/shared/sharedFacilityLogic'
import { getTheme } from '../../../redux/user/userSlice'
import { selectStyleDark, selectStyleLight } from '../../../utils/sharedCareTeamLogic'
import { getIsAi } from '../../../utils/sharedFeaturesFunctions'

export default function FacilityInfoCard({
  setSelectedFacility,
  selectedFacilityId,
  setSelectedFacilityId,
  showPOS,
  pos,
  onNewPOSSelected,
  disabled = false,
  telehealth,
  children,
}) {
  const dispatch = useDispatch()
  // this is always true, which always shows a completed place of service section
  // area for refactoring or spec updating
  const [facilityChosen, setFacilityChosen] = useState(true)
  const [allPOSList, setAllPOSList] = useState([])
  const [posList, setPosList] = useState([])

  const allFacilities = useSelector((state) => state.census?.allFacilitiesList)
  const theme = useSelector(getTheme)

  const updatePlacesOfService = useCallback(() => {
    const currentFacility = allFacilities?.find(
      (singleFacility) => singleFacility._id === selectedFacilityId,
    )
    const currentFacilityDefaultPlaceOfServiceCode = currentFacility?.place_of_service
    const currentFacilityDefaultPlaceOfServiceObject = allPOSList?.find(
      (item) => item.code === currentFacilityDefaultPlaceOfServiceCode,
    )

    // order matters. The first element should be the default element (if it exists)
    let tempPlacesOfService = []
    if (currentFacilityDefaultPlaceOfServiceObject) {
      tempPlacesOfService = [currentFacilityDefaultPlaceOfServiceObject]
    }

    // get all the place of service id's from the facility that are on the master list
    // (just do not re-append the default element)
    allPOSList?.forEach((item) => {
      if (
        currentFacility?.places_of_service?.includes(item?.code) &&
        item?.code !== currentFacilityDefaultPlaceOfServiceObject?.code
      ) {
        tempPlacesOfService.push(item)
      }
    })

    // store the current POS list
    setPosList(tempPlacesOfService)

    // if the facility does have some optional places of service
    // AND the current place of service is not on the list,
    // THEN update the place of service to the 0th (default)
    if (
      tempPlacesOfService.length > 0 &&
      !tempPlacesOfService.find((tempPOS) => tempPOS.code === pos)
    ) {
      onNewPOSSelected(tempPlacesOfService[0]?.code)
    }
  }, [allFacilities, allPOSList, selectedFacilityId, onNewPOSSelected, pos])

  useEffect(() => {
    dispatch(getFacilities())
    listPlaceOfService().then((res) => {
      if (res?.status === 'success') {
        setAllPOSList(res.results)
      }
    })
  }, [dispatch])

  useEffect(() => {
    updatePlacesOfService()
  }, [selectedFacilityId, allPOSList, updatePlacesOfService])

  const handleSelectedFacilityData = (newFacility) => {
    setSelectedFacility(newFacility?.name)
    setSelectedFacilityId(newFacility?.facility_id)

    onNewPOSSelected('')
    updatePlacesOfService()
  }

  const toggleTelehealth = (telehealth) => {
    const currentFacility = allFacilities?.find(
      (singleFacility) => singleFacility._id === selectedFacilityId,
    )
    const res = updateTelehealthPOS(currentFacility, posList, telehealth)

    if (!isEmpty(res)) {
      onNewPOSSelected(res.place_of_service)
    }
  }

  useEffect(() => {
    toggleTelehealth(telehealth)
  }, [telehealth])

  const selectOptions = allFacilities.map((singleFacility) => {
    return {
      value: singleFacility?.facility_id,
      label: (
        <div>
          {singleFacility?.name}
          {getPOSDisplay(singleFacility)}
        </div>
      ),
    }
  })

  return (
    <CCard className="PlaceofService">
      <div className="encounter-details-CardHeader">
        <div className="tittle-holder">
          {facilityChosen ? (
            <CImage className="tick-size-pos" src={getIsAi() ? aiTick : tick} />
          ) : (
            <span className="encounter-progress-number nonactive-number-progress-encounter"></span>
          )}
          <p>Facility Info</p>
        </div>

        {facilityChosen && <span className="completed-container completed-text">COMPLETED</span>}
      </div>

      <div
        className={'facility-info-data'}
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          position: 'relative',
        }}
      >
        <div className={'facility-wrapper'} style={{ width: '50%' }}>
          {disabled && <div className={'locked'}></div>}
          <div className="encounter-details-facility">Facility</div>
          <Select
            className="encounter-selectbox-input break-important"
            classNamePrefix={'facSelect'}
            styles={theme === 'light' ? selectStyleLight : selectStyleDark}
            onChange={({ value }) => {
              const chosenFacility = allFacilities.find((fac) => fac?._id === value)
              handleSelectedFacilityData(chosenFacility)
              setFacilityChosen(true)
            }}
            value={selectOptions?.find(({ value }) => value === selectedFacilityId)}
            options={selectOptions}
            isSearchable={false}
            menuShouldScrollIntoView={false}
            isDisabled={disabled}
          />
        </div>
        {showPOS && posList?.length > 1 && (
          <div className={'pos-wrapper'} style={{ width: '50%' }}>
            <div className="encounter-details-facility">Place of Service</div>
            <span>
              <PlaceOfServiceSelector
                listPOS={posList}
                onPOSSelect={onNewPOSSelected}
                value={pos}
                disabled={!pos || disabled}
                theme={theme}
              />
            </span>
          </div>
        )}
      </div>
      {children}
    </CCard>
  )
}
FacilityInfoCard.propTypes = {
  setSelectedFacility: PropTypes.any,
  selectedFacilityId: PropTypes.string,
  setSelectedFacilityId: PropTypes.any,
  showPOS: PropTypes.bool,
  pos: PropTypes.string,
  onNewPOSSelected: PropTypes.any,
  children: PropTypes.any,
}
